import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Ticket,
  Clock,
  Users,
  CheckCircle,
  AlertCircle,
  Loader2,
  Building2,
  Phone,
  RefreshCw,
  Volume2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const QueueTicket = () => {
  const { queueId, ticketId } = useParams();
  const [loading, setLoading] = useState(true);
  const [queueStatus, setQueueStatus] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [taking, setTaking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  const [form, setForm] = useState({
    patient_name: '',
    patient_phone: '',
    notes: ''
  });

  const loadQueueStatus = useCallback(async () => {
    if (!queueId) return;
    try {
      const response = await api.get(`/queue/${queueId}/status`);
      setQueueStatus(response.data);
    } catch (error) {
      console.error('Error loading queue:', error);
      toast.error('File non trouvée');
    } finally {
      setLoading(false);
    }
  }, [queueId]);

  const loadTicketStatus = useCallback(async () => {
    if (!ticketId) return;
    try {
      const response = await api.get(`/queue/ticket/${ticketId}`);
      setTicket(response.data);
    } catch (error) {
      console.error('Error loading ticket:', error);
      toast.error('Ticket non trouvé');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (ticketId) {
      loadTicketStatus();
      const interval = setInterval(loadTicketStatus, 5000);
      return () => clearInterval(interval);
    } else if (queueId) {
      loadQueueStatus();
    }
  }, [queueId, ticketId, loadQueueStatus, loadTicketStatus]);

  const handleTakeTicket = async (e) => {
    e.preventDefault();
    if (!form.patient_name) {
      toast.error('Veuillez entrer votre nom');
      return;
    }
    
    setTaking(true);
    try {
      const response = await api.post(`/queue/${queueId}/ticket`, form);
      setTicket({
        ticket: response.data.ticket,
        position: response.data.ticket.position || 1,
        estimated_wait_minutes: response.data.ticket.estimated_wait_minutes || 15,
        now_serving: response.data.ticket.now_serving || 0,
        queue_name: queueStatus?.queue?.name,
        facility_name: queueStatus?.queue?.facility_name
      });
      toast.success('Ticket créé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la prise du ticket');
    } finally {
      setTaking(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    if (ticketId || ticket?.ticket?.id) {
      await loadTicketStatus();
    } else {
      await loadQueueStatus();
    }
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A237E] to-[#3949AB] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
    );
  }

  // If we have a ticket, show ticket status
  if (ticket) {
    const isMyTurn = ticket.ticket?.status === 'serving' || ticket.position === 0;
    const isCalled = ticket.ticket?.status === 'called';
    const isCompleted = ticket.ticket?.status === 'completed';
    const isCancelled = ticket.ticket?.status === 'cancelled' || ticket.ticket?.status === 'no_show';

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A237E] to-[#3949AB] py-8 px-4">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center text-white mb-6">
            <Building2 className="w-12 h-12 mx-auto mb-2 opacity-80" />
            <h1 className="text-xl font-bold">{ticket.facility_name || ticket.ticket?.facility_name}</h1>
            <p className="text-white/70 text-sm">{ticket.queue_name || ticket.ticket?.queue_name}</p>
          </div>

          {/* Ticket Card */}
          <Card className={`overflow-hidden ${
            isMyTurn ? 'ring-4 ring-green-400 animate-pulse' : ''
          }`}>
            <div className={`p-4 text-center ${
              isMyTurn ? 'bg-green-500' : 
              isCalled ? 'bg-amber-500' :
              isCompleted ? 'bg-blue-500' :
              isCancelled ? 'bg-slate-500' :
              'bg-red-500'
            } text-white`}>
              {isMyTurn && (
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Volume2 className="w-6 h-6 animate-bounce" />
                  <span className="text-lg font-bold">C&apos;EST VOTRE TOUR !</span>
                </div>
              )}
              {isCalled && (
                <div className="flex items-center justify-center gap-2 mb-2">
                  <AlertCircle className="w-6 h-6" />
                  <span className="text-lg font-bold">VOUS ÊTES APPELÉ</span>
                </div>
              )}
              {isCompleted && (
                <div className="flex items-center justify-center gap-2 mb-2">
                  <CheckCircle className="w-6 h-6" />
                  <span className="text-lg font-bold">TERMINÉ</span>
                </div>
              )}
              {isCancelled && (
                <div className="flex items-center justify-center gap-2 mb-2">
                  <XCircle className="w-6 h-6" />
                  <span className="text-lg font-bold">ANNULÉ</span>
                </div>
              )}
              <p className="text-white/80 text-sm">Votre numéro</p>
              <p className="text-6xl font-bold my-2">{ticket.ticket?.ticket_number}</p>
              <p className="font-medium">{ticket.ticket?.patient_name}</p>
            </div>

            <CardContent className="p-6">
              {!isCompleted && !isCancelled && (
                <>
                  {/* Current Number */}
                  <div className="text-center mb-6">
                    <p className="text-slate-500 text-sm">Numéro en cours</p>
                    <p className="text-4xl font-bold text-slate-900">{ticket.now_serving || 0}</p>
                  </div>

                  {/* Position & Wait Time */}
                  {!isMyTurn && (
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center p-4 bg-slate-50 rounded-xl">
                        <Users className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-slate-900">{ticket.position}</p>
                        <p className="text-xs text-slate-500">Personnes devant vous</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-xl">
                        <Clock className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-slate-900">~{ticket.estimated_wait_minutes}</p>
                        <p className="text-xs text-slate-500">Minutes d&apos;attente</p>
                      </div>
                    </div>
                  )}

                  {/* Refresh Button */}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleRefresh}
                    disabled={refreshing}
                  >
                    {refreshing ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Actualiser
                  </Button>

                  <p className="text-center text-xs text-slate-400 mt-4">
                    Mise à jour automatique toutes les 5 secondes
                  </p>
                </>
              )}

              {(isCompleted || isCancelled) && (
                <div className="text-center py-4">
                  <p className="text-slate-500">
                    {isCompleted ? 'Merci pour votre visite !' : 'Votre ticket a été annulé.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show queue status and take ticket form
  if (!queueStatus?.queue?.is_active) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A237E] to-[#3949AB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">File fermée</h2>
            <p className="text-slate-500">
              Cette file d&apos;attente n&apos;accepte plus de nouveaux tickets pour le moment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A237E] to-[#3949AB] py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center text-white mb-6">
          <Building2 className="w-12 h-12 mx-auto mb-2 opacity-80" />
          <h1 className="text-xl font-bold">{queueStatus?.queue?.facility_name}</h1>
          <p className="text-white/70">{queueStatus?.queue?.name}</p>
        </div>

        {/* Queue Status Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-slate-500 text-sm">Numéro actuel</p>
                <p className="text-4xl font-bold text-red-500">{queueStatus?.queue?.now_serving || 0}</p>
              </div>
              <div>
                <p className="text-slate-500 text-sm">En attente</p>
                <p className="text-4xl font-bold text-slate-900">{queueStatus?.waiting_count || 0}</p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-amber-50 rounded-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              <span className="text-sm text-amber-700">
                Temps d&apos;attente estimé: ~{(queueStatus?.waiting_count || 0) * (queueStatus?.queue?.estimated_service_time || 15)} min
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Take Ticket Form */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Ticket className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h2 className="font-bold text-lg">Prendre un ticket</h2>
                <p className="text-sm text-slate-500">Remplissez vos informations</p>
              </div>
            </div>

            <form onSubmit={handleTakeTicket} className="space-y-4">
              <div>
                <Label>Votre nom *</Label>
                <Input
                  value={form.patient_name}
                  onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                  placeholder="Nom complet"
                  className="mt-1 h-12"
                  required
                />
              </div>

              <div>
                <Label>Téléphone (optionnel)</Label>
                <Input
                  value={form.patient_phone}
                  onChange={(e) => setForm({ ...form, patient_phone: e.target.value })}
                  placeholder="6XXXXXXXX"
                  className="mt-1 h-12"
                />
              </div>

              <div>
                <Label>Notes (optionnel)</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Motif de la visite"
                  className="mt-1 h-12"
                />
              </div>

              <Button
                type="submit"
                disabled={taking}
                className="w-full h-14 text-lg bg-red-500 hover:bg-red-600"
              >
                {taking ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Ticket className="w-5 h-5 mr-2" />
                    Obtenir mon ticket
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-white/50 text-xs mt-6">
          Powered by DOCKHTA
        </p>
      </div>
    </div>
  );
};

export default QueueTicket;
